import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { ChatAppResponse, getCitationFilePath } from "../../api";
import { Warning16Regular } from "@fluentui/react-icons";

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
};

// Function to validate if a citation candidate (text from within brackets) is a valid citation
// based on the provided context data points.
function isCitationValid(contextDataPoints: any, citationCandidate: string): boolean {
    // Regex to check for a file-like pattern (e.g., name.ext or name.ext#fragment).
    // This ensures the citation candidate has a structure resembling a filename with an extension.
    const filePatternRegex = /.+\.\w+(?:#\S*)?$/; // \w+ is equivalent to \w{1,}
    if (!filePatternRegex.test(citationCandidate)) {
        // If the candidate doesn't look like a filename with an extension,
        // it's not considered a valid citation format according to this rule.
        return false;
    }

    // Extract base filenames from contextDataPoints (removing any #fragments)
    let dataPointsBaseFilenames: string[];
    if (Array.isArray(contextDataPoints)) {
        dataPointsBaseFilenames = contextDataPoints.map((dp: string) => dp.split("#")[0]);
    } else if (contextDataPoints && Array.isArray(contextDataPoints.text)) {
        // Fallback for a potential alternative structure of contextDataPoints
        dataPointsBaseFilenames = (contextDataPoints.text as string[]).map((dp: string) => dp.split("#")[0]);
    } else {
        // No data points provided or in an unexpected format
        return false;
    }

    if (dataPointsBaseFilenames.length === 0) {
        return false; // No data points to validate against
    }

    // Normalize the citation candidate by removing any #fragment
    const normalizedCandidateFilename = citationCandidate.split("#")[0];

    // Check if the normalized candidate filename exactly matches any of the normalized data point filenames
    const isValidCitation = dataPointsBaseFilenames.includes(normalizedCandidateFilename);

    return isValidCitation;
}

export function parseAnswerToHtml(
    answer: ChatAppResponse,
    isStreaming: boolean,
    onCitationClicked: (citationFilePath: string) => void,
    t: (key: string) => string
): HtmlParsedAnswer {
    // This function parses the answer from the ChatAppResponse and converts it into an HTML string.
    // It handles citations by extracting them from the answer and creating clickable links.
    // The function also handles streaming responses by omitting citations that are still being typed.
    // It returns an object containing the parsed HTML string and an array of citations.

    // Ensure contextDataPoints is an array for isCitationValid, even if undefined in answer.context.
    // isCitationValid handles empty or improperly structured contextDataPoints.
    const contextDataPoints = answer.context?.data_points || [];
    const citations: string[] = [];

    // Trim any whitespace from the end of the answer after removing follow-up questions
    let parsedAnswer = answer.message.content.trim();

    // Omit a citation that is still being typed during streaming
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        const truncatedAnswer = parsedAnswer.substring(0, lastIndex);
        parsedAnswer = truncatedAnswer;
    }

    // Split the answer into parts based on the citation format [citation]
    // The regex /\[([^\]]+)\]/g captures the content inside the square brackets
    // and splits the string accordingly.
    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);

    // Iterate through the parts and construct the HTML string
    // Even indices are text parts, odd indices are citations
    // If a citation is not valid, it is returned as is
    // If a citation is valid, it is added to the citations array and a link is created
    // If a citation is already in the citations array, the existing index is used
    const fragments: string[] = parts.map((part, index) => {
        if (index % 2 === 0) {
            return part;
        } else {
            let citationIndex: number;

            if (!isCitationValid(contextDataPoints, part)) {
                return `[${part}]`;
            }

            if (citations.indexOf(part) !== -1) {
                citationIndex = citations.indexOf(part) + 1;
            } else {
                citations.push(part);
                citationIndex = citations.length;
            }

            const path = getCitationFilePath(part);

            return renderToStaticMarkup(
                <a className="supContainer" title={part} onClick={() => onCitationClicked(path)}>
                    <sup>{citationIndex}</sup>
                </a>
            );
        }
    });

    let answerHtml = fragments.join("");
    if (!isStreaming && answerHtml) {
        const warningMessage = renderToStaticMarkup(
            <span style={{ display: "flex", alignItems: "center", gap: "4px", fontStyle: "italic", color: "#D46F00" }}>
                <Warning16Regular />
                {t("answerInaccurateWarning")}
            </span>
        );
        answerHtml += `<br><br>${warningMessage}`;
    }

    return {
        answerHtml,
        citations
    };
}
